import { storage } from '@uhg-abyss/web/tools/storage';
import { useEffect, useRef } from 'react';

import { Constants, DEFAULT_SEARCH_RADIUS } from '../../common/Constants';
import { ProviderSearchRequestParams } from '../../models/ProviderSearch';
import {
  getBehavioralFacilityPrograms,
  getFilteredBHFacilityProgramKeywordValue,
} from '../../utils/providerSearch.utils';

/**
 * Helper to determine search radius based on filters
 * @param filterParams - Parsed filter parameters
 * @param currentRadius - Current search radius value
 * @param autoSearchRadius - Auto search radius from filters
 * @returns Calculated search radius value
 */
export const determineSearchRadius = (
  filterParams,
  currentRadius,
  autoSearchRadius
) => {
  if (filterParams?.virtualCare && !filterParams?.searchRadius) {
    return -1;
  }
  return !Number.isNaN(currentRadius)
    ? currentRadius
    : !Number.isNaN(autoSearchRadius)
    ? autoSearchRadius
    : DEFAULT_SEARCH_RADIUS;
};

/**
 * Resolves the networkAffiliationId from the typeahead store or session storage fallback.
 * Wraps a plain string in an array; passes arrays through unchanged.
 * Returns undefined when no value is present.
 */
export const resolveNetworkAffiliationId = (
  storeValue: string[] | undefined,
  sessionValue: string | string[] | null | undefined
): string[] | undefined => {
  const raw = storeValue || sessionValue;
  if (!raw) return undefined;
  return Array.isArray(raw) ? raw : [raw];
};

/**
 * Custom hook that captures the networkAffiliationId from the typeahead store
 * (with session-storage fallback), clears session storage after capture,
 * and returns the resolved value as a string array (or undefined).
 */
export const useNetworkAffiliationSearchId = (
  networkAffiliationId: string[] | undefined
): string[] | undefined => {
  const sessionValue = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.NETWORK_AFFILIATION_SEARCH_ID
  );
  const resolved = resolveNetworkAffiliationId(
    networkAffiliationId,
    sessionValue
  );
  const ref = useRef(resolved);

  useEffect(() => {
    if (ref.current) {
      storage.session.remove(
        Constants.STORAGE_KEYS.SESSION.NETWORK_AFFILIATION_SEARCH_ID
      );
    }
  }, []);

  return ref.current;
};

export const getBehavioralFacilityProgramsValue = (
  enableCategoryCountV2: boolean,
  searchFromTypeAhead: boolean | undefined,
  behavioralFacilityPrograms: any,
  bhFacilityPrograms: string,
  filterParams: ProviderSearchRequestParams
) => {
  const bhFacilityProgramsValue = enableCategoryCountV2
    ? ''
    : searchFromTypeAhead
    ? getFilteredBHFacilityProgramKeywordValue(bhFacilityPrograms, filterParams)
    : getBehavioralFacilityPrograms(filterParams, behavioralFacilityPrograms);
  return Array.from(
    new Set([
      ...bhFacilityProgramsValue.split(',').map((program) => program.trim()),
      ...(filterParams?.behavioralFacilityPrograms
        ?.split(',')
        .map((program) => program.trim()) || []),
    ])
  ).join(',');
};
